import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { adsUserLogin, validateAdsUsername } from '../../utils/api';

const AdsLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const validateUsername = async () => {
    const trimmed = username.trim();
    if (!trimmed) {
      return { valid: false, message: 'Username is required' };
    }

    setIsCheckingUsername(true);
    try {
      await validateAdsUsername(trimmed);
      return { valid: true };
    } catch (err) {
      return {
        valid: false,
        message: err.response?.data?.message || 'Please enter a valid username'
      };
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleLogin = async () => {
    if (!username || !password) {
      setError('Username and password are required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const usernameCheck = await validateUsername();
      if (!usernameCheck.valid) {
        setError(usernameCheck.message || 'Please enter a valid username');
        return;
      }

      const response = await adsUserLogin({ username, password });
      const { user, token } = response.data || {};

      if (!token || !user) {
        throw new Error('Invalid login response from server');
      }

      localStorage.setItem('adToken', token);
      localStorage.setItem('adUser', JSON.stringify(user));

      const resolvedUserId =
        user?.userId ??
        user?.id ??
        null;

      if (resolvedUserId !== null && resolvedUserId !== undefined) {
        localStorage.setItem('adUserId', resolvedUserId.toString());
      } else {
        localStorage.removeItem('adUserId');
      }

      const from = location.state?.from?.pathname || '/ads/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Ads login error:', err);
      setError(err.response?.data?.message || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Typography variant="h5" gutterBottom align="center">
          Ad Creator Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Username"
          variant="outlined"
          margin="normal"
          fullWidth
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onBlur={validateUsername}
          disabled={isLoading || isCheckingUsername}
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          margin="normal"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleLogin}
          fullWidth
          sx={{ mt: 2 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Login'}
        </Button>
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          New user?{' '}
          <Link component="button" type="button" onClick={() => navigate('/ads/signup')}>
            Sign up
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default AdsLogin;
